// Lightweight, privacy-friendly client tracking for Survive on BCH.
//
// Sends anonymous event counts to our own Netlify Function (/.netlify/functions/stats).
// No cookies, no personal data — just increments a counter server-side.
// Fails silently: if the backend isn't deployed (e.g. local dev), nothing breaks.

const ENDPOINT = "/.netlify/functions/stats?action=track";

export type TrackEvent =
  | "visit"
  | "survival_start"
  | "hustle_start"
  | "game_complete"
  | "share_nostr"
  | "share_x";

interface TrackPayload {
  event: TrackEvent;
  /** For survival_start: the chosen currency mode ("BCH" | "BTC"). */
  mode?: string;
}

export function track(event: TrackEvent, extra?: { mode?: string }): void {
  // Only run in the browser.
  if (typeof window === "undefined") return;

  const payload: TrackPayload = { event, ...extra };
  const body = JSON.stringify(payload);

  try {
    // sendBeacon is ideal — non-blocking and survives page navigation.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch {
    // fall through to fetch
  }

  // Fallback: fire-and-forget fetch.
  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    /* ignore — tracking must never break the app */
  });
}

/**
 * Track a page visit at most once per browser session (per tab session),
 * so a player clicking around doesn't inflate the visit count.
 */
export function trackVisitOnce(): void {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem("survive-on-bch:visited") === "1") return;
    sessionStorage.setItem("survive-on-bch:visited", "1");
  } catch {
    // sessionStorage may be unavailable; still count the visit.
  }
  track("visit");
}
